import { useMemo } from "react";

export default function MerchandisingSummary({
  locale,
  merchandisingFilters,
  filteredMerchandisingAlertRows,
  filteredMerchandisingQueueRows,
  filteredMerchandisingSourcingBoard,
  scopedWorkspaceMetrics,
  merchandisingReadinessBreakdown,
  merchandisingCategorySummary,
  merchandisingMarketSummary,
  merchandisingRecommendationBoard,
  merchandisingView
}) {
  const activeMerchandisingFilterCount = useMemo(
    () => Object.values(merchandisingFilters).filter(Boolean).length,
    [merchandisingFilters]
  );

  const merchandisingHeroMetrics = useMemo(() => ([
    {
      key: "products",
      label: locale === "zh" ? "覆盖商品" : "Coverage",
      value: scopedWorkspaceMetrics.productCount || 0,
      note: locale === "zh" ? "纳入决策视角的商品总量" : "Products currently in the decision view"
    },
    {
      key: "ready",
      label: locale === "zh" ? "可推进" : "Ready to Push",
      value: scopedWorkspaceMetrics.readyForLaunchCount || 0,
      note: locale === "zh" ? "利润与素材条件相对成熟" : "Margin and readiness conditions look investable"
    },
    {
      key: "alerts",
      label: locale === "zh" ? "当前风险" : "Current Risk",
      value: filteredMerchandisingAlertRows.length,
      note: locale === "zh" ? "筛选后仍会拖慢推进节奏的问题" : "Issues still slowing decision velocity after filters"
    },
    {
      key: "filters",
      label: locale === "zh" ? "推进名单" : "Focus List",
      value: filteredMerchandisingQueueRows.length,
      note: activeMerchandisingFilterCount
        ? (locale === "zh" ? `${activeMerchandisingFilterCount} 个条件生效` : `${activeMerchandisingFilterCount} active filters`)
        : (locale === "zh" ? "未额外收窄观察范围" : "No additional narrowing applied")
    }
  ]), [
    activeMerchandisingFilterCount,
    filteredMerchandisingAlertRows.length,
    filteredMerchandisingQueueRows.length,
    locale,
    scopedWorkspaceMetrics.productCount,
    scopedWorkspaceMetrics.readyForLaunchCount
  ]);

  const merchandisingSummaryMaxima = useMemo(() => ({
    readiness: Math.max(1, ...merchandisingReadinessBreakdown.map((item) => Number(item.count || 0))),
    category: Math.max(1, ...merchandisingCategorySummary.map((item) => Number(item.count || 0))),
    market: Math.max(1, ...merchandisingMarketSummary.map((item) => Number(item.count || 0))),
    recommendation: Math.max(1, ...merchandisingRecommendationBoard.map((item) => Number(item.count || 0)))
  }), [
    merchandisingCategorySummary,
    merchandisingMarketSummary,
    merchandisingReadinessBreakdown,
    merchandisingRecommendationBoard
  ]);

  const merchandisingExecutiveQueue = useMemo(
    () => filteredMerchandisingQueueRows
      .slice()
      .sort((left, right) => {
        const leftScore = Number(left.grossMarginRate || 0) * 3 + Number(left.readinessScore || 0) * 8 - (Array.isArray(left.selectionAlerts) ? left.selectionAlerts.length * 10 : 0);
        const rightScore = Number(right.grossMarginRate || 0) * 3 + Number(right.readinessScore || 0) * 8 - (Array.isArray(right.selectionAlerts) ? right.selectionAlerts.length * 10 : 0);
        return rightScore - leftScore;
      })
      .slice(0, 8),
    [filteredMerchandisingQueueRows]
  );

  const merchandisingExecutiveActions = useMemo(() => {
    const actions = [];
    if ((scopedWorkspaceMetrics.samplePendingCount || 0) > 0) {
      actions.push({
        key: "samples",
        title: locale === "zh" ? "优先清样品确认" : "Clear sample confirmation first",
        count: scopedWorkspaceMetrics.samplePendingCount || 0,
        tone: "warning"
      });
    }
    if ((scopedWorkspaceMetrics.compliancePendingCount || 0) > 0) {
      actions.push({
        key: "compliance",
        title: locale === "zh" ? "补齐合规文件" : "Complete compliance documents",
        count: scopedWorkspaceMetrics.compliancePendingCount || 0,
        tone: "danger"
      });
    }
    if ((scopedWorkspaceMetrics.readyForLaunchCount || 0) > 0) {
      actions.push({
        key: "ready",
        title: locale === "zh" ? "把成熟商品推进到报价/上架评审" : "Advance mature SKUs into pricing and launch review",
        count: scopedWorkspaceMetrics.readyForLaunchCount || 0,
        tone: "positive"
      });
    }
    return actions.slice(0, 3);
  }, [
    locale,
    scopedWorkspaceMetrics.compliancePendingCount,
    scopedWorkspaceMetrics.readyForLaunchCount,
    scopedWorkspaceMetrics.samplePendingCount
  ]);

  const merchandisingDrawerTabs = useMemo(() => ([
    {
      key: "risk",
      label: locale === "zh" ? "风险与筛选" : "Risk & Filters",
      count: filteredMerchandisingAlertRows.length
    },
    {
      key: "decision",
      label: locale === "zh" ? "利润与市场" : "Margin & Market",
      count: filteredMerchandisingSourcingBoard.length
    },
    {
      key: "focus",
      label: locale === "zh" ? "重点名单" : "Focus List",
      count: merchandisingExecutiveQueue.length
    }
  ]), [
    filteredMerchandisingAlertRows.length,
    filteredMerchandisingSourcingBoard.length,
    locale,
    merchandisingExecutiveQueue.length
  ]);

  const activeMerchandisingDrawerTab = useMemo(
    () => merchandisingDrawerTabs.find((item) => item.key === merchandisingView) || merchandisingDrawerTabs[0],
    [merchandisingDrawerTabs, merchandisingView]
  );

  const merchandisingViewTabs = useMemo(() => ([
    {
      key: "overview",
      label: locale === "zh" ? "总览" : "Overview",
      count: merchandisingExecutiveQueue.length
    },
    ...merchandisingDrawerTabs
  ]), [locale, merchandisingDrawerTabs, merchandisingExecutiveQueue.length]);

  return {
    merchandisingHeroMetrics,
    merchandisingSummaryMaxima,
    merchandisingExecutiveQueue,
    merchandisingExecutiveActions,
    merchandisingDrawerTabs,
    activeMerchandisingDrawerTab,
    merchandisingViewTabs
  };
}
